import { useState, useEffect, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import versionService from '../../services/versionService';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import {
  isDeploymentRelatedError,
  getSessionStatus,
  getCheckedInTimeLabel,
  extractWeekNumber,
  getTimeRangeLabel,
  isCheckInWindowActive,
  isLiveSessionActive,
  getVirtualTimeRangeLabel
} from './activeAttendanceSessionsHelpers';

export default function useActiveAttendanceSessions() {
  const { user } = useAuth();
  // Depend on id/role, not the user object reference -- AuthContext hands out
  // a new object (same underlying user) on every token refresh, which was
  // re-triggering this effect and causing duplicate fetches on load.
  const userId = user?.id;
  const userRole = user?.role;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const deploymentErrorCountRef = useRef(0);
  const pollingPausedRef = useRef(false);

  // Check if an update is available when deployment-related errors occur
  const checkForUpdate = useCallback(async () => {
    try {
      const hasUpdate = await versionService.checkForUpdates();
      if (hasUpdate) {
        setUpdateAvailable(true);
        pollingPausedRef.current = true; // Stop polling when update is detected
        logger.info('App update detected during attendance fetch - pausing polling');
      }
      return hasUpdate;
    } catch (e) {
      logger.warn('Failed to check for updates', { error: e });
      return false;
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    // Don't fetch if user is null or not a student
    if (!userId || userRole !== 'student') {
      setSessions([]);
      return;
    }

    // Don't fetch if polling is paused due to detected update
    if (pollingPausedRef.current) {
      logger.debug('Polling paused - update available');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Always add cache-busting parameter to ensure fresh data
      const endpoint = '/attendance/active-today?v=' + Date.now();
      const response = await apiService.request(endpoint);

      console.log('📍 Active Sessions API Response:', response);

      if (response.success) {
        setSessions(response.sessions || []);
        console.log('📍 Sessions set:', response.sessions?.length || 0, 'sessions');
        // Reset error count on successful fetch
        deploymentErrorCountRef.current = 0;
      } else {
        setError(response.message || 'Failed to fetch active sessions');
        console.error('📍 API Error:', response.message);
      }
    } catch (error) {
      // Handle permission errors gracefully - don't show error for non-students
      if (error.status === 403) {
        logger.debug('User does not have permission to access attendance sessions');
        setSessions([]);
        setError('');
        return;
      }

      // Check if this might be a deployment-related error
      if (isDeploymentRelatedError(error)) {
        deploymentErrorCountRef.current += 1;
        logger.warn('Possible deployment-related error in attendance fetch', {
          errorCount: deploymentErrorCountRef.current,
          error: error.message
        });

        // After 2 consecutive deployment-like errors, check for updates
        if (deploymentErrorCountRef.current >= 2) {
          const hasUpdate = await checkForUpdate();
          if (hasUpdate) {
            setError('');
            return; // Don't show error, update banner will be shown
          }
        }

        // Show a more helpful error message for deployment scenarios
        if (deploymentErrorCountRef.current >= 3) {
          setError('Connection interrupted. The app may be updating - please refresh the page.');
        } else {
          // Don't show error for first couple of failures - might be transient
          setError('');
        }
      } else {
        setError('Failed to fetch active sessions. Please try again.');
        deploymentErrorCountRef.current = 0;
      }
    } finally {
      setLoading(false);
    }
  }, [userId, userRole, checkForUpdate]);


  useEffect(() => {
    if (userId) {
      // Always fetch fresh data when component mounts
      fetchActiveSessions();

      // Refresh when user returns to the tab (focus event)
      const handleFocus = () => {
        logger.debug('Page focused - refreshing sessions');
        fetchActiveSessions();
      };

      // Refresh when page becomes visible (visibilitychange event)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          logger.debug('Page visible - refreshing sessions');
          fetchActiveSessions();
        }
      };

      // Periodic refresh every 2 minutes to catch admin updates
      const interval = setInterval(() => {
        logger.debug('Periodic refresh - checking for session updates');
        fetchActiveSessions();
      }, 120000); // 2 minutes

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Clear sessions when user is null
      setSessions([]);
    }
  }, [userId, fetchActiveSessions]);



  // Filter sessions to show until check-in window ends
  const getVisibleSessions = () => {
    if (!sessions || sessions.length === 0) {
      console.log('📍 No sessions available');
      return [];
    }

    const now = DateTime.now().setZone('America/Chicago');
    console.log('📍 Current time:', now.toISO());
    console.log('📍 Total sessions:', sessions.length);

    const visibleSessions = sessions.filter(session => {
      if (!session.end_time) {
        console.log('📍 Session filtered (no end_time):', session.session_id);
        return false;
      }

      // Parse the end_time to determine when check-in closes
      let endTime;
      try {
        if (typeof session.end_time === 'string') {
          endTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        } else {
          endTime = DateTime.fromJSDate(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        }
      } catch (e) {
        console.log('📍 Session filtered (parse error):', session.session_id, e.message);
        return false;
      }

      if (!endTime.isValid) {
        console.log('📍 Session filtered (invalid endTime):', session.session_id);
        return false;
      }

      const isVisible = now <= endTime;
      console.log('📍 Session:', session.session_id, 'end_time:', endTime.toISO(), 'visible:', isVisible);
      return isVisible;
    });

    console.log('📍 Visible sessions:', visibleSessions.length);
    return visibleSessions;
  };

  const handleCheckIn = async (sessionId) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      setError('');
      setSuccess('');

      const response = await apiService.checkInAttendance(sessionId, 'present');

      if (response.success) {
        setSuccess('Successfully checked in for attendance!');

        // Refresh sessions to get the latest data from the server
        logger.debug('Check-in successful - refreshing sessions');
        await fetchActiveSessions();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Use the specific error message from the API response
        setError(response.message || response.error || 'Failed to check in for attendance');
      }
    } catch (error) {
      logger.error('Error checking in', { error, sessionId });
      // Try to extract meaningful error message from the error response
      let errorMessage = 'Failed to check in for attendance. Please try again.';

      // First, try to get message from response object
      if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.error) {
        errorMessage = error.response.error;
      } else if (error?.message) {
        // If the message looks like JSON, try to parse it
        if (typeof error.message === 'string' && error.message.includes('{')) {
          try {
            const parsed = JSON.parse(error.message);
            errorMessage = parsed.message || parsed.error || error.message;
          } catch (e) {
            // If parsing fails, use the original message
            errorMessage = error.message;
          }
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setCheckingIn(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  return {
    sessions,
    loading,
    error,
    success,
    checkingIn,
    updateAvailable,
    getVisibleSessions,
    handleCheckIn,
    getSessionStatus,
    getCheckedInTimeLabel,
    extractWeekNumber,
    getTimeRangeLabel,
    isCheckInWindowActive,
    isLiveSessionActive,
    getVirtualTimeRangeLabel
  };
}
